import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import {
    AppLanguage,
    LANGUAGE_LOCALES,
    LANGUAGE_STORAGE_KEY,
    translations,
} from '@/i18n/translations';

type TranslationValues = Record<string, number | string>;

interface I18nContextValue {
    language: AppLanguage;
    locale: string;
    setLanguage: (language: AppLanguage) => void;
    t: (key: string, values?: TranslationValues) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function interpolateTranslation(
    message: string,
    values?: TranslationValues,
): string {
    if (!values) {
        return message;
    }

    return Object.entries(values).reduce((result, [key, value]) => {
        return result.replaceAll(`{${key}}`, String(value));
    }, message);
}

function getInitialLanguage(): AppLanguage {
    if (typeof window === 'undefined') {
        return 'de';
    }

    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

    return storedLanguage === 'de' || storedLanguage === 'en' || storedLanguage === 'es'
        ? storedLanguage
        : 'de';
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [language, setLanguage] = useState<AppLanguage>(getInitialLanguage);

    useEffect(() => {
        document.documentElement.lang = language;
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }, [language]);

    const value = useMemo<I18nContextValue>(() => ({
        language,
        locale: LANGUAGE_LOCALES[language],
        setLanguage,
        t: (key, values) => {
            const translation = translations[language][key] ?? key;

            return interpolateTranslation(translation, values);
        },
    }), [language]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
    const context = useContext(I18nContext);

    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider.');
    }

    return context;
}
